import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";

/**
 * Renders the published House Rules from the database.
 *
 * One component, used by the canonical /house-rules page, the policies page and
 * (next) the booking flow. Every surface therefore shows the same text from the
 * same published version. The alternative - each page holding its own copy - is
 * exactly what produced a site saying quiet hours were 10:00 PM while the emails
 * said 8:00 PM.
 *
 * "summary" renders headings only with a link to the full page, so /policies can
 * point at the rules without publishing a second full copy of them at a second
 * URL.
 */

export interface LegalDocument {
  slug: string;
  title: string;
  versionNumber: number;
  contentHash: string;
  effectiveFrom: string | null;
  publishedAt: string | null;
  sections: { heading: string; body: string; anchor: string | null }[];
}

export function useHouseRules() {
  return useQuery<LegalDocument>({
    queryKey: ["/api/legal/house_rules"],
    retry: false,
  });
}

/** Split a stored section body back into its intro / bullets / note parts. */
function parseBody(body: string) {
  const lines = body.split("\n").filter(Boolean);
  const intro = lines[0] && !lines[0].startsWith("- ") ? lines[0] : null;
  const items = lines.filter((l) => l.startsWith("- ")).map((l) => l.slice(2));
  const tail = lines[lines.length - 1];
  const note = tail && !tail.startsWith("- ") && tail !== intro ? tail : null;
  return { intro, items, note };
}

export function HouseRulesDisplay({
  variant = "full",
  className = "",
}: {
  variant?: "full" | "summary";
  className?: string;
}) {
  const { data, isLoading, isError } = useHouseRules();

  if (isLoading) {
    return <p className={`text-ink-soft font-sans text-[15px] ${className}`}>Loading house rules…</p>;
  }

  // Never fail silently here. If the rules cannot be shown, say so and point at
  // a human, rather than rendering an empty box that looks like there are none.
  if (isError || !data) {
    return (
      <p className={`text-ink-soft font-sans text-[15px] ${className}`}>
        Our house rules could not be loaded just now. Please{" "}
        <Link href="/contact" className="text-ink border-b border-sand-deep hover:border-sun transition-colors">contact us</Link>{" "}
        and we will send them to you directly.
      </p>
    );
  }

  if (variant === "summary") {
    return (
      <div className={className}>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mb-6">
          {data.sections.map((s) => (
            <li key={s.heading} className="flex gap-3 text-ink-soft font-sans text-[15px] leading-relaxed">
              <span className="text-driftwood">-</span>
              {s.heading}
            </li>
          ))}
        </ul>
        <Link
          href="/house-rules"
          className="inline-flex min-h-[24px] items-center text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300"
          data-testid="house-rules-full-link"
        >
          Read the full house rules
        </Link>
      </div>
    );
  }

  return (
    <div className={className} data-testid="house-rules-full">
      {data.sections.map((s) => {
        const { intro, items, note } = parseBody(s.body);
        return (
          <section key={s.heading} id={s.anchor ?? undefined} className="mb-10 scroll-mt-28">
            <h2 className="text-luxury-lg font-serif text-forest mb-4">{s.heading}</h2>
            {intro && <p className="text-ink-soft font-sans leading-relaxed mb-4">{intro}</p>}
            {items.length > 0 && (
              <ul className="space-y-3">
                {items.map((item) => (
                  <li key={item} className="flex gap-3 text-ink-soft font-sans leading-relaxed">
                    <span className="text-driftwood shrink-0">-</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {note && <p className="text-ink-soft font-sans leading-relaxed mt-4 italic">{note}</p>}
          </section>
        );
      })}

      <p className="mt-12 pt-6 border-t border-sand-deep text-[13px] text-driftwood font-sans">
        House Rules version {data.versionNumber}
        {data.effectiveFrom && ` · effective ${new Date(data.effectiveFrom).toLocaleDateString("en-US", { dateStyle: "long", timeZone: "UTC" })}`}
      </p>
    </div>
  );
}
